"use client";

import {
  Pencil,
  FileInput,
  RotateCw,
  Files,
  Wrench,
  FilePlus,
  Trash2,
  ChevronDown,
  FlaskConical,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import DeleteTokenModal from "./DeleteTokenModal";
import { fetchUserAppkeys, deleteAppkey, updateAppkey } from "@/lib/tokenApi";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useRouter } from "next/navigation";
import EditTokenModal from "./EditTokenModal";
import logger from "@/lib/logger";
import useClientTranslation from "@/hooks/useClientTranslation";

interface Token {
  // 现有界面依赖的字段（保留，避免破坏 UI）
  id: string; // 改为必需
  name: string;
  group: string; // 显示用的分组名称
  groupId: string; // 用于过滤的分组 ID（等同 app_id）
  // 兼容 billing -> billing_info 的迁移：两者任意存在即可
  billing_info?: string;
  billing?: string;
  status: string; // "正常" | "禁用"
  quota: number | string; // 原始额度（number）或展示字符串；显示时除以 500000
  used: number | string; // 后端提供的纯数字或展示字符串（以最小单位或 cents），显示时除以 500000
  model: string; // 展示用字符串（逗号分隔）
  created: string;
  limit: string;
  ip_whitelist?: string[];

  // 新结构的标准字段（供后续优化使用）
  app_id?: string; // 分组 ID
  disabled?: boolean; // 是否禁用
  models?: string[]; // 可用模型列表
  consumed?: number; // 消耗额度（float）
  quotaNum?: number; // 剩余额度（float）
  generateCount?: number; // 生成数量（int）
  numericId?: number; // 后端数据库主键 ID，用于删除/更新等操作
}

interface Props {
  onAdd?: () => void;
  filters?: { name?: string; billing?: string; status?: string };
  groupId?: string | null;
  onRefresh?: () => void;
  refreshCount?: number; // 接收刷新信号
  tokens?: Token[]; // 来自父组件的令牌列表
  onTokensChange?: (tokens: Token[]) => void; // 令牌变化回调
  onDelete?: (tokenId: string) => Promise<void>; // 删除令牌回调
  onUpdate?: (tokenId: string, updates: Partial<Token>) => Promise<void>; // 更新令牌回调
  onGetDetail?: (tokenId: string) => Promise<Token>; // 获取令牌详情回调
}

/** 将 new-api 扁平格式令牌转换为 Token[] */
function transformFlatTokens(items: unknown[]): Token[] {
  return items.filter(Boolean).map(item => {
    const r = item as Record<string, unknown>;
    const id = r.id != null ? String(r.id) : "";
    const key = typeof r.key === "string" ? r.key : "";
    const name = typeof r.name === "string" ? r.name : "未命名令牌";
    const group = typeof r.group === "string" ? r.group : "default";
    const status = r.status === 1 ? "normal" : "disabled";
    const unlimitedQuota = r.unlimited_quota === true;
    const remainQuota = typeof r.remain_quota === "number" ? r.remain_quota : 0;
    const usedQuota = typeof r.used_quota === "number" ? r.used_quota : 0;
    const createdTime = typeof r.created_time === "number"
      ? new Date(r.created_time * 1000).toLocaleDateString()
      : "-";
    const expiredTime = typeof r.expired_time === "number" && r.expired_time > 0
      ? new Date(r.expired_time * 1000).toLocaleDateString()
      : "";
    const modelLimits = typeof r.model_limits === "string" && r.model_limits
      ? r.model_limits : "";
    const allowIps = typeof r.allow_ips === "string" && r.allow_ips
      ? r.allow_ips.split(",").map(s => s.trim()).filter(Boolean)
      : [];

    const applicationId = r.application_id != null ? String(r.application_id) : "";

    return {
      id: key || id,
      numericId: typeof r.id === "number" ? r.id : Number(r.id) || undefined,
      name,
      group,
      groupId: applicationId || group,
      billing_info: unlimitedQuota ? "unlimited" : "limited",
      billing: unlimitedQuota ? "unlimited" : "limited",
      status,
      quota: unlimitedQuota ? "无限制" : remainQuota,
      used: usedQuota,
      model: modelLimits,
      created: createdTime,
      limit: expiredTime,
      ip_whitelist: allowIps,
      app_id: applicationId || group,
      disabled: status !== "normal",
      models: modelLimits ? modelLimits.split(",").map(s => s.trim()) : [],
      consumed: usedQuota,
      quotaNum: unlimitedQuota ? NaN : remainQuota,
      generateCount: 1,
    };
  });
}

/** 将旧嵌套格式（apps -> appkeys）转换为 Token[]（保留原有逻辑） */
function transformNestedTokens(apps: unknown[]): Token[] {
  const result: Token[] = [];
  apps.forEach(app => {
    if (!app || typeof app !== "object") return;
    const appRec = app as Record<string, unknown>;
    const appId = typeof appRec.app_id === "string"
      ? appRec.app_id : String(appRec.app_id ?? "default-local");
    const appName = typeof appRec.app_name === "string" && appRec.app_name.trim()
      ? appRec.app_name.trim() : "默认分组";
    const appKeys = Array.isArray(appRec.appkeys) ? appRec.appkeys as unknown[] : [];
    appKeys.forEach(key => {
      if (!key || typeof key !== "object") return;
      const k = key as Record<string, unknown>;
      const billing = (k.billing_info ?? k.billing) as Record<string, unknown> | undefined;
      const onModels = Array.isArray(k.on_models) ? k.on_models as unknown[] : [];
      const modelsArr = onModels.map(m => {
        if (m && typeof m === "object" && "model_name" in (m as Record<string, unknown>))
          return String((m as Record<string, unknown>).model_name);
        return String(m);
      });
      const rawConsumed = billing && typeof billing.consumed_amount === "number"
        ? Number(billing.consumed_amount) : 0;
      const quotaFloat = billing && billing.quota_limit !== undefined
        ? Number(billing.quota_limit) : NaN;
      const quota = billing && billing.quota_enabled === false ? "无限制" : quotaFloat;
      result.push({
        id: typeof k.appkey === "string" ? k.appkey : String(k.appkey ?? ""),
        name: typeof k.name === "string" ? k.name : "未命名令牌",
        group: appName, groupId: appId,
        billing_info: "unlimited", billing: "unlimited",
        status: typeof k.alive === "boolean" ? (k.alive ? "normal" : "disabled") : "disabled",
        quota, used: rawConsumed,
        model: modelsArr.join(", "),
        created: typeof k.created_at === "string" ? new Date(k.created_at).toLocaleDateString() : "-",
        limit: typeof k.expires_at === "string" ? new Date(k.expires_at).toLocaleDateString() : "",
        ip_whitelist: Array.isArray(k.ip_whitelist) ? (k.ip_whitelist as string[]).map(String) : [],
        app_id: appId, disabled: typeof k.alive === "boolean" ? !k.alive : undefined,
        models: modelsArr, consumed: rawConsumed, quotaNum: quotaFloat, generateCount: 1,
      });
    });
  });
  return result;
}

export default function TokenTable({
  onAdd,
  filters = {},
  groupId,
  onRefresh,
  refreshCount = 0,
  tokens: externalTokens = [],
  onTokensChange,
}: Props) {
  const { t } = useClientTranslation("tokenpg");
  const [selectedTokens, setSelectedTokens] = useState<Set<number>>(new Set());
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTokenId, setSelectedTokenId] = useState<number | null>(null);
  const [tokens, setTokens] = useState<Token[]>([]); // 本地令牌列表
  const [editingToken, setEditingToken] = useState<Token | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const router = useRouter();
  const refreshTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [refreshTurns, setRefreshTurns] = useState(0);

  // 同步外部令牌列表
  useEffect(() => {
    if (externalTokens && externalTokens.length > 0) {
      setTokens(externalTokens);
    }
  }, [externalTokens]);

  const [loading, setLoading] = useState(false);

  // 从 API 获取令牌数据
  useEffect(() => {
    const fetchTokens = async () => {
      // 如果有外部令牌则不需要获取
      if (externalTokens && externalTokens.length > 0) {
        return;
      }

      // 等待 sidebar 选择分组后再请求
      if (!groupId || groupId === "default-local") {
        return;
      }

      try {
        setLoading(true);
        const isAllTokens = groupId === "__all__";
        const data = await fetchUserAppkeys(isAllTokens ? undefined : groupId);

        const items = Array.isArray(data) ? data : [];
        const isNewApiFlat = items.length > 0 && items[0] && typeof items[0] === "object" && "key" in (items[0] as Record<string, unknown>);

        const transformedTokens: Token[] = isNewApiFlat
          ? transformFlatTokens(items)
          : transformNestedTokens(items);

        // "全部令牌"模式下不按分组过滤，否则后端可能未按 application_id 过滤，前端兜底过滤
        const filtered = isAllTokens
          ? transformedTokens
          : groupId
            ? transformedTokens.filter(t => t.groupId === groupId || t.app_id === groupId)
            : transformedTokens;

        setTokens(filtered);
        onTokensChange?.(filtered);
      } catch (err: unknown) {
        if (err instanceof Error) {
          logger.error("获取令牌失败:", err);
          toast.error(t("messages.fetchFailed") + ": " + err.message);
        } else {
          logger.error("获取令牌失败:", String(err));
          toast.error(t("messages.fetchFailed"));
        }
        setTokens([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTokens();
  }, [refreshCount, groupId]); // 监听 refreshCount 和 groupId 变化以重新获取

  const mockData: Token[] = tokens;

  // 简单过滤：根据 TokenFilters 的字段进行字符串包含匹配
  const filtered = mockData.filter(row => {
    if (filters.name && !row.name.includes(filters.name)) return false;
    if (filters.billing) {
      const billingText = row.billing_info?.startsWith("limited:")
        ? `${t("quotaLimited")} ${row.billing_info.slice(8)}`
        : row.billing_info === "unlimited"
        ? t("quotaUnlimited")
        : row.billing_info || row.billing || "";
      if (!billingText.includes(filters.billing)) return false;
    }
    if (filters.status) {
      const statusText = row.status === "normal" ? t("statusNormal") : t("statusDisabled");
      if (!statusText.includes(filters.status)) return false;
    }
    return true;
  });

  /**
   * 切换多选模式
   */
  const handleToggleMultiSelect = () => {
    setMultiSelectMode(!multiSelectMode);
    setSelectedTokens(new Set()); // 切换时清空已选
  };

  /**
   * 选择/取消选择单个令牌
   */
  const handleSelectToken = (index: number) => {
    const newSelected = new Set(selectedTokens);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedTokens(newSelected);
  };

  /**
   * 全选/取消全选
   */
  const handleSelectAll = () => {
    if (selectedTokens.size === filtered.length) {
      setSelectedTokens(new Set());
    } else {
      setSelectedTokens(new Set(filtered.map((_, i) => i)));
    }
  };

  /**
   * 导出令牌信息为 CSV（支持中文）
   */
  const handleExport = () => {
    // 如果有选中的令牌，导出选中的；否则导出所有
    const dataToExport =
      selectedTokens.size > 0 ? Array.from(selectedTokens).map(i => filtered[i]) : filtered;

    const headers = [
      t("exportHeaders.tokenName"),
      t("exportHeaders.status"),
      t("exportHeaders.availableModels"),
      t("exportHeaders.consumed"),
      t("exportHeaders.remaining"),
      t("exportHeaders.createdAt"),
      t("exportHeaders.expiresAt"),
    ];

    const rows = dataToExport.map(row => {
      const billingText =
        typeof row.quota === "number" && isFinite(row.quota)
          ? `${t("quotaLimited")} ${formatUsedForDisplay(row.quota)}`
          : t("quotaUnlimited");
      return [
        row.name,
        billingText,
        row.status === "normal" ? t("statusNormal") : t("statusDisabled"),
        formatUsedForDisplay(row.used),
        formatUsedForDisplay(row.quota),
        row.created,
        row.limit || t("longTermValid"),
      ];
    });

    // 构建 CSV 内容，使用 BOM 字符确保中文正确显示
    const BOM = "\uFEFF";
    const csvContent = [
      headers.map(h => `"${h}"`).join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `tokens-${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  /**
   * 复制令牌
   */
  const handleCopy = (rowIndex: number) => {
    const tokenRow = filtered[rowIndex];
    // 后端返回字段为 appkey（小写），界面兼容 id 存储
    const maybeRow = tokenRow as unknown as Record<string, unknown>;
    const appkey = typeof maybeRow.appkey === "string" ? maybeRow.appkey : tokenRow.id || "";
    if (!appkey) {
      toast.error(t("messages.copyFailed"));
      return;
    }

    navigator.clipboard.writeText(appkey).then(() => {
      toast.success(t("messages.copySuccess"));
    });
  };

  /**
   * 重置令牌
   */
  const handleReset = (rowIndex: number) => {
    // console.log("重置令牌", rowIndex); // debug only
    toast.success(t("messages.resetSuccess"));
  };

  /**
   * 编辑令牌
   */
  const handleEdit = (rowIndex: number) => {
    const tokenRow = filtered[rowIndex];
    setEditingToken(tokenRow);
    setShowEditModal(true);
  };

  const handleDisable = async (rowIndex: number) => {
    const tokenRow = filtered[rowIndex];
    try {
      setLoading(true);
      await updateAppkey(tokenRow.id, { alive: false, numericId: tokenRow.numericId });
      setTokens(prev => prev.map(token => (token.id === tokenRow.id ? { ...token, status: "disabled" } : token)));
      toast.success(t("messages.disableSuccess"));
    } catch (err: unknown) {
      if (err instanceof Error) {
        logger.error("禁用令牌失败:", err);
        toast.error(t("messages.disableFailed") + ": " + err.message);
      } else {
        logger.error("禁用令牌失败:", String(err));
        toast.error(t("messages.disableFailed"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEnable = async (rowIndex: number) => {
    const tokenRow = filtered[rowIndex];
    try {
      setLoading(true);
      await updateAppkey(tokenRow.id, { alive: true, numericId: tokenRow.numericId });
      setTokens(prev => prev.map(token => (token.id === tokenRow.id ? { ...token, status: "normal" } : token)));
      toast.success(t("messages.enableSuccess"));
    } catch (err: unknown) {
      if (err instanceof Error) {
        logger.error("启用令牌失败:", err);
        toast.error(t("messages.enableFailed") + ": " + err.message);
      } else {
        logger.error("启用令牌失败:", String(err));
        toast.error(t("messages.enableFailed"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTestRequest = (rowIndex: number) => {
    const tokenRow = filtered[rowIndex];
    const maybeRow = tokenRow as unknown as Record<string, unknown>;
    const appkey = typeof maybeRow.appkey === "string" ? maybeRow.appkey : tokenRow.id || "";
    router.push(`/testpg?appkey=${encodeURIComponent(appkey)}`);
  };

  const handleViewLogs = () => {
    router.push("/logpg");
  };

  /**
   * 删除令牌
   */
  const handleDeleteToken = (rowIndex: number) => {
    setSelectedTokenId(rowIndex);
    setShowDeleteModal(true);
  };

  /**
   * 确认删除令牌
   */
  const handleConfirmDelete = async () => {
    if (selectedTokenId === null) return;

    try {
      const deletedToken = filtered[selectedTokenId];

      setLoading(true);
      // 调用后端 API 删除令牌
      const tokenId = deletedToken.numericId ?? Number(deletedToken.id);
      if (!tokenId || isNaN(tokenId)) throw new Error("无法获取令牌 ID");
      await deleteAppkey(tokenId);

      // console.log("删除令牌成功:", deletedToken);

      // 从本地 tokens 列表中移除
      const updatedTokens = tokens.filter(t => t.id !== deletedToken.id);
      setTokens(updatedTokens);

      // 通知父组件 tokens 已更新
      onTokensChange?.(updatedTokens);

      toast.success(t("messages.deleteSuccess"));
      setShowDeleteModal(false);
      setSelectedTokenId(null);
    } catch (err: unknown) {
      if (err instanceof Error) {
        logger.error("删除令牌失败:", err);
        toast.error(t("messages.deleteFailed") + ": " + err.message);
      } else {
        logger.error("删除令牌失败:", String(err));
        toast.error(t("messages.deleteFailed"));
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * 刷新令牌列表
   */
  const handleRefresh = () => {
    setRefreshTurns(t => t + 1);
    // 连续点击只触发最后一次（尾随防抖）
    if (refreshTimeout.current) {
      clearTimeout(refreshTimeout.current);
    }

    refreshTimeout.current = setTimeout(() => {
      onRefresh?.();
    }, 300);
  };

  const formatUsedForDisplay = (used: number | string | undefined) => {
    const num =
      typeof used === "number" ? used : Number(String(used ?? "").replace(/[^0-9.-]+/g, ""));
    if (!isFinite(num)) return "-";

    const divided = num / 500000;
    const formatted = Number.isInteger(divided)
      ? String(divided)
      : // 保留最多 6 位小数并去掉尾部无意义的 0
        divided.toFixed(6).replace(/\.0+$|(?<=\.[0-9]*[1-9])0+$/g, "");

    return formatted;
  };

  useEffect(() => {
    return () => {
      if (refreshTimeout.current) {
        clearTimeout(refreshTimeout.current);
      }
    };
  }, []);

  return (
    <>
      <Card className="pb-32">
        <CardHeader className="flex-col gap-3 sm:flex-row items-start sm:items-center justify-between space-y-0">
          <CardTitle className="text-lg">{t("tokenManagement")}</CardTitle>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={handleToggleMultiSelect}>
              <Pencil size={14} />
              {multiSelectMode ? t("cancelMultiSelect") : t("multiSelect")}
            </Button>
            <Button size="sm" onClick={handleExport}>
              <FileInput size={14} /> {t("export")}
            </Button>
            <Button size="sm" onClick={onAdd}>
              <FilePlus size={14} /> {t("add")}
            </Button>
            <Button variant="outline" size="sm" onClick={handleRefresh}>
              <RotateCw
                size={14}
                style={{
                  transform: `rotate(${refreshTurns * 360}deg)`,
                  transition: "transform 0.6s ease",
                }}
              />
              {t("refresh")}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto -mx-6 px-6">
          <Table className="min-w-[800px]">
            <TableHeader>
              <TableRow>
                {multiSelectMode && (
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedTokens.size === filtered.length && filtered.length > 0}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                )}
                <TableHead>{t("tableHeaders.tokenName")}</TableHead>
                <TableHead>{t("tableHeaders.status")}</TableHead>
                <TableHead>{t("tableHeaders.availableModels")}</TableHead>
                <TableHead>{t("tableHeaders.consumed")}</TableHead>
                <TableHead>{t("tableHeaders.quotaLimit")}</TableHead>
                <TableHead>{t("tableHeaders.createdAt")}</TableHead>
                <TableHead>{t("tableHeaders.expiresAt")}</TableHead>
                <TableHead className="w-24">{t("tableHeaders.actions")}</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={multiSelectMode ? 9 : 8}
                    className="h-24 text-center text-muted-foreground"
                  >
                    {t("noTokens")}
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((row, i) => (
                  <TableRow
                    key={i}
                    data-state={selectedTokens.has(i) ? "selected" : undefined}
                  >
                    {multiSelectMode && (
                      <TableCell className="w-12">
                        <Checkbox
                          checked={selectedTokens.has(i)}
                          onCheckedChange={() => handleSelectToken(i)}
                        />
                      </TableCell>
                    )}
                    <TableCell>{row.name}</TableCell>
                    <TableCell>
                      <Badge variant={row.status === "disabled" ? "destructive" : "default"}>
                        {row.status === "normal" ? t("statusNormal") : t("statusDisabled")}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {row.model.includes(",") ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Badge variant="outline" className="cursor-pointer gap-1">
                              {t("availableModelsLabel")}
                              <ChevronDown size={12} />
                            </Badge>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="start"
                            className="w-48 max-h-48 overflow-y-auto"
                          >
                            {row.model.split(",").map((model, idx) => (
                              <DropdownMenuItem key={idx} className="text-sm break-words">
                                {model.trim()}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <Badge variant="outline">
                          {row.model || t("allModels")}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>{formatUsedForDisplay(row.used)}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="text-purple-700 dark:text-purple-400">
                        {typeof row.quota === "number" && isFinite(row.quota)
                          ? `${t("quotaLimited")} ${formatUsedForDisplay(row.quota)}`
                          : t("quotaUnlimited")}
                      </Badge>
                    </TableCell>
                    <TableCell>{row.created}</TableCell>
                    <TableCell>{row.limit || t("longTermValid")}</TableCell>
                    <TableCell>
                      <div className="flex gap-3">
                        <span title={t("actions.copy")} className="cursor-help">
                          <Files
                            size={16}
                            className="cursor-pointer text-primary hover:text-primary/80"
                            onClick={() => handleCopy(i)}
                          />
                        </span>
                        <span title={t("actions.testRequest")} className="cursor-help">
                          <FlaskConical
                            size={16}
                            className="cursor-pointer text-primary hover:text-primary/80"
                            onClick={() => handleTestRequest(i)}
                          />
                        </span>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <span title={t("tableHeaders.actions")} className="cursor-help">
                              <Wrench
                                size={16}
                                className="cursor-pointer text-primary hover:text-primary/80"
                              />
                            </span>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-36">
                            {row.status === "disabled" ? (
                              <DropdownMenuItem onClick={() => handleEnable(i)}>
                                {t("actions.enable")}
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => handleDisable(i)}>
                                {t("actions.disable")}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handleEdit(i)}>{t("actions.edit")}</DropdownMenuItem>
                            <DropdownMenuItem onClick={handleViewLogs}>{t("actions.logs")}</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <span title={t("actions.delete")} className="cursor-help">
                          <Trash2
                            size={16}
                            className="cursor-pointer text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                            onClick={() => handleDeleteToken(i)}
                          />
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>

      <DeleteTokenModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        tokenName={selectedTokenId !== null ? filtered[selectedTokenId]?.name : ""}
      />

      {editingToken && (
        <EditTokenModal
          open={showEditModal}
          onClose={setShowEditModal}
          onSuccess={updated => {
            if (updated.id) {
              const next = tokens.map(t => (t.id === updated.id ? updated : t));
              setTokens(next);
              Promise.resolve().then(() => {
                onTokensChange?.(next);
              });
              setTimeout(() => {
                handleRefresh();
              }, 300);
            }
          }}
          token={editingToken}
        />
      )}

    </>
  );
}
